import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  ColorPicker,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Dropdown,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import { CheckCircleFilled, EllipsisOutlined, ExclamationCircleFilled, InboxOutlined } from '@ant-design/icons';
import Layout from 'components/Layout';
import StripePaymentModal from 'components/StripePaymentModal';
import { AdminContext } from 'constants/contexts';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  applyPlatformBillingPreset,
  bootstrapPlatformDeliveryFees,
  bootstrapPlatformSms,
  createOrgSetupIntent,
  createPlatformSmsClient,
  deleteOrgPaymentMethod,
  deletePlatformBillingPlan,
  deletePlatformSmsClient,
  fetchOrgPaymentMethods,
  fetchPlatformBillingPlan,
  fetchPlatformOrganization,
  fetchPlatformSmsClients,
  invitePlatformRdsOwner,
  markPlatformGoLive,
  patchPlatformBillingPlan,
  pausePlatformBillingPlan,
  resumePlatformBillingPlan,
  setPlatformOrganizationOwner,
  updatePlatformOrganization,
  updatePlatformBranding,
  updatePlatformSmsClient,
  upsertPlatformBillingPlan,
} from 'api/platform';
import {
  createNewUserPermission,
  fetchUsersPermissions,
} from 'containers/AccountSettings/UserManagement/requests';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import {
  omniSupportPhoneRule,
  phoneDigitsForInput,
  requiredPhoneRule,
  toE164Phone,
  toOmniSupportPhone,
} from 'utils';
import { openConsoleForOrg, persistOrganizationSelection } from 'utils/organization';
import StripeContainer from 'containers/AccountSettings/Subscription/StripeContainer';
import TabNote from 'containers/Platform/TabNote';
import {
  GO_LIVE_REQUIRED,
  StatusTag,
  StatusTags,
  descriptionsLabelStyle,
  formItemStyle,
  goLiveMarkProps,
  optionalLabel,
  platformFormClassName,
  sectionCardProps,
  sectionStackGap,
  switchFieldProps,
  tabContentStyle,
  formTooltip,
  tipLines,
} from 'containers/Platform/labels';
import { isAcceptableLogoUrl, logoFileRejectReason, shouldPreviewLogoFileInMemory } from 'containers/Platform/logoUrl';
import DispatchersTab from 'containers/Platform/Clients/DispatchersTab';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const { Text } = Typography;

const CLIENT_TAB_KEYS = [
  'overview',
  'general',
  'setup',
  'dispatchers',
  'branding',
  'stripe',
  'billing',
  'sms',
  'people',
];

/**
 * Platform client button conventions:
 * - Primary CTA (save / create / setup / invite): type="primary", default size
 * - Secondary CTA (cancel / alternate / open console): default type + size
 * - Table row actions only: type="link" size="small"
 */

const SMS_PRIORITY_OPTIONS = [
  { value: 4, label: 'Urgent (4)', color: 'error' },
  { value: 3, label: 'High (3)', color: 'warning' },
  { value: 2, label: 'Medium (2)', color: 'processing' },
  { value: 1, label: 'Low (1)', color: 'default' },
];

const SMS_DRIVER_LABELS = {
  twilio: 'Twilio',
  'amazon sns': 'Amazon SNS',
  debug: 'Debug',
};

function smsPriorityMeta(priority) {
  return SMS_PRIORITY_OPTIONS.find((o) => o.value === Number(priority))
    || { value: priority, label: String(priority ?? '—'), color: 'default' };
}

function smsDriverLabel(driver) {
  if (!driver) return '—';
  return SMS_DRIVER_LABELS[driver] || driver;
}

function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '—';
}

const BILLING_COMMISSIONS = [
  {
    key: 'omnimerchant_commission',
    label: 'OmniMerchant commission',
    extra: 'Charged on qualifying POS-integrated order value (e.g. Toast).',
  },
  {
    key: 'gfo_commission',
    label: 'GFO commission',
    extra: 'Charged on Google Food Ordering orders.',
  },
  {
    key: 'dispatch_commission',
    label: 'Dispatch commission',
    extra: 'Charged on delivery orders.',
  },
  {
    key: 'self_delivery_commission',
    label: 'Self-delivery commission',
    extra: 'Charged on self-delivery orders.',
  },
  {
    key: 'catering_commission',
    label: 'Catering commission',
    extra: 'Percentage applies to catering subtotal.',
  },
];

function flattenCommission(commission) {
  return {
    percentage: Number(commission?.percentage) || 0,
    fixed: (Number(commission?.fixed_cents) || 0) / 100,
  };
}

function flattenPlanCommissions(plan) {
  const next = { ...(plan || {}) };
  BILLING_COMMISSIONS.forEach(({ key }) => {
    const flat = flattenCommission(plan?.[key]);
    next[`${key}_percentage`] = flat.percentage;
    next[`${key}_fixed`] = flat.fixed;
  });
  return next;
}

function collectPlanCommissions(values) {
  const payload = { ...values };
  BILLING_COMMISSIONS.forEach(({ key }) => {
    payload[key] = {
      percentage: payload[`${key}_percentage`] ?? 0,
      fixed_cents: Math.round(Number(payload[`${key}_fixed`] || 0) * 100),
    };
    delete payload[`${key}_percentage`];
    delete payload[`${key}_fixed`];
  });
  return payload;
}

function commissionIsCharged(commission) {
  if (!commission) return false;
  return Number(commission.percentage) > 0 || Number(commission.fixed_cents) > 0;
}

function planRequiresCard(plan) {
  if (!plan) return false;
  if (Number(plan.base_price) > 0 || Number(plan.catering_price) > 0) return true;
  if (Number(plan.map_price_per_address) > 0) return true;
  if (BILLING_COMMISSIONS.some(({ key }) => {
    if (plan[`${key}_percentage`] != null || plan[`${key}_fixed`] != null) {
      return Number(plan[`${key}_percentage`]) > 0 || Number(plan[`${key}_fixed`]) > 0;
    }
    return commissionIsCharged(plan[key]);
  })) return true;
  return BILLING_COMMISSIONS.some(({ key }) => commissionIsCharged(plan[key]));
}

function CommissionInputs({ percentageName, fixedName }) {
  return (
    <Row gutter={8}>
      <Col span={12}>
        <Text type="secondary">Percentage</Text>
        <Form.Item name={percentageName} noStyle>
          <InputNumber
            style={{ width: '100%', marginTop: 4 }}
            min={0}
            max={100}
            step={0.1}
            addonAfter="%"
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Text type="secondary">Fixed amount</Text>
        <Form.Item name={fixedName} noStyle>
          <InputNumber
            style={{ width: '100%', marginTop: 4 }}
            min={0}
            step={0.01}
            prefix="$"
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

function CommissionFields() {
  return BILLING_COMMISSIONS.map(({ key, label, extra }) => (
    <Col xs={24} sm={12} key={key}>
      <Form.Item
        label={label}
        extra={`${extra} Enter a percentage, a fixed amount, or both.`}
        style={formItemStyle}
      >
        <CommissionInputs
          percentageName={`${key}_percentage`}
          fixedName={`${key}_fixed`}
        />
      </Form.Item>
    </Col>
  ));
}

function distance(value) {
  const miles = Number(value);
  return Number.isFinite(miles) ? `${miles.toFixed(2)} mi` : '—';
}

/** "3% + $2.00", or "Not charged" when both parts are zero. */
function commissionLabel(commission) {
  const pct = Number(commission?.percentage) || 0;
  const fixed = (Number(commission?.fixed_cents) || 0) / 100;
  const parts = [];
  if (pct) parts.push(`${pct}%`);
  if (fixed) parts.push(money(fixed));
  return parts.join(' + ') || 'Not charged';
}

function days(value) {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return '—';
  return `${count} ${count === 1 ? 'day' : 'days'}`;
}

/**
 * Read-only view of a saved plan. Charged commissions are listed individually and
 * the rest collapse into one line, so a $0 plan stays short.
 */
function billingPlanSummaryRows(plan, { isCatering }) {
  const rows = [{ key: 'base', label: 'Base price', value: money(plan.base_price) }];
  if (isCatering) {
    rows.push({ key: 'catering_price', label: 'Catering price', value: money(plan.catering_price) });
  }
  rows.push(
    { key: 'orders', label: 'Included orders', value: plan.base_number_of_orders ?? 0 },
    { key: 'extra', label: 'Price per extra order', value: money(plan.price_per_extra_order) },
    { key: 'cycle', label: 'Billing cycle', value: days(plan.billing_cycle_days) },
    { key: 'deadline', label: 'Invoice payment deadline', value: days(plan.invoices_deadline_days) },
  );
  if (Number(plan.map_price_per_address) > 0) {
    rows.push({
      key: 'map',
      label: 'Map price per address',
      value: money(plan.map_price_per_address),
    });
  }

  const charged = BILLING_COMMISSIONS.filter(({ key }) => commissionIsCharged(plan[key]));
  charged.forEach(({ key, label }) => {
    rows.push({ key, label, value: commissionLabel(plan[key]) });
  });
  if (charged.length < BILLING_COMMISSIONS.length) {
    rows.push({
      key: 'uncharged',
      label: charged.length ? 'Other commissions' : 'Commissions',
      value: 'Not charged',
    });
  }

  rows.push({
    key: 'card',
    label: 'Payment card',
    value: planRequiresCard(plan) ? 'Required' : 'Not required',
  });
  return rows;
}

/** Borderless label/value pairs for plan + fee summaries. */
function KeyValueTable({ rows, style }) {
  return (
    <div
      role="table"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 1fr) auto',
        columnGap: 24,
        rowGap: 10,
        marginBottom: 16,
        ...style,
      }}
    >
      {rows.map((row) => (
        <React.Fragment key={row.key}>
          <Text type="secondary" role="rowheader">{row.label}</Text>
          <Text strong style={{ textAlign: 'right' }}>{row.value}</Text>
        </React.Fragment>
      ))}
    </div>
  );
}

/** "$5.00 up to 3.00 mi, then $1.50/mi" */
function feeSentence(values) {
  if (!values) return null;
  return `${money(values.constant_amount)} up to ${distance(values.max_constant_distance)}, `
    + `then ${money(values.additional_mile_amount)}/mi`;
}

const FEE_COMPARE_KEYS = ['constant_amount', 'additional_mile_amount', 'max_constant_distance'];

function formulaMatchesDefaults(formula, defaults) {
  if (!formula || !defaults) return false;
  return FEE_COMPARE_KEYS.every((key) => Number(formula[key]) === Number(defaults[key]));
}

function requiredItemsLabel(count) {
  return `${count} required ${count === 1 ? 'item' : 'items'} remaining`;
}

function markBlockedReason(count) {
  if (!count) return 'Finish the required checklist items below first.';
  return `Finish the ${count} required ${count === 1 ? 'item' : 'items'} below first.`;
}

/** e.g. "Aug 14, 2026 at 8:00 AM -04:00" — offset so this is not read as UTC. */
function formatGoLiveAt(value) {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY [at] h:mm A Z') : String(value);
}

function GoLiveAction({ markedAt, ready, missingCount, marking, onMark }) {
  if (markedAt) {
    const when = formatGoLiveAt(markedAt);
    return (
      <Text type="secondary">
        {when ? `Marked for go-live ${when}` : 'Marked for go-live'}
      </Text>
    );
  }

  const blocked = !ready;
  const reason = blocked ? markBlockedReason(missingCount) : undefined;
  const button = (
    <Button
      type="primary"
      loading={marking}
      disabled={blocked}
      onClick={onMark}
      aria-label={blocked ? `Mark ready for go-live. ${reason}` : undefined}
    >
      Mark ready for go-live
    </Button>
  );
  if (!blocked) {
    return button;
  }
  return (
    <Tooltip title={reason}>
      <span>{button}</span>
    </Tooltip>
  );
}

const ACTION_LABELS = {
  console_location: 'Open location',
  console_restaurant: 'Open restaurant',
  console_restaurant_owner: 'Open restaurant',
  console_menus: 'Open menus',
  setup: 'Open delivery fees',
  general: 'Open general',
  dispatchers: 'Open dispatchers',
  branding: 'Open branding',
  stripe: 'Open Stripe',
  billing: 'Open billing',
  sms: 'Open SMS',
  people: 'Open people',
};

/**
 * Items that lead to the same destination share one button, so a section never
 * repeats "Open restaurant" once per row.
 */
function groupByDestination(items) {
  const groups = [];
  const byLabel = new Map();
  items.forEach((item) => {
    const buttonLabel = ACTION_LABELS[item.action] || 'Review';
    const key = item.action ? buttonLabel : `no-action-${item.key}`;
    let group = byLabel.get(key);
    if (!group) {
      group = { key, buttonLabel, hasAction: Boolean(item.action), items: [] };
      byLabel.set(key, group);
      groups.push(group);
    }
    group.items.push(item);
  });
  return groups;
}

function SecretMeta({ meta }) {
  if (!meta?.is_set) return <Text type="secondary">Not set</Text>;
  if (meta.is_valid === false) return <Text type="danger">Invalid value — replace it</Text>;
  return <Text type="secondary">Set ·····{meta.last4}</Text>;
}

function OverviewTab({
  org, onOpenConsole, onSwitchTab, onMarkedGoLive,
}) {
  const r = org.readiness || {};
  const sections = r.sections || {};
  const deepLinks = r.deep_links || {};
  const { setOrganization } = useContext(AdminContext);
  const navigate = useNavigate();
  const [marking, setMarking] = useState(false);

  const openConsole = (path) => {
    openConsoleForOrg({
      org,
      setOrganization,
      navigate,
      path,
    });
  };

  const handleAction = (item) => {
    const action = item.action;
    if (!action) return;
    if (action === 'console_location') {
      openConsole(deepLinks.parent_id
        ? `/console/locations/${deepLinks.parent_id}`
        : '/console/locations/create');
      return;
    }
    if (action === 'console_restaurant') {
      openConsole(deepLinks.restaurant_id
        ? `/console/restaurants/${deepLinks.restaurant_id}`
        : '/console/restaurants/create');
      return;
    }
    if (action === 'console_restaurant_owner') {
      openConsole(deepLinks.restaurant_id
        ? `/console/restaurants/${deepLinks.restaurant_id}`
        : '/console/restaurants/create');
      return;
    }
    if (action === 'console_menus') {
      if (org.global_menus_enabled) {
        openConsole('/console/menus');
      } else if (deepLinks.restaurant_id) {
        openConsole(`/console/restaurants/${deepLinks.restaurant_id}/menus`);
      } else {
        openConsole('/console/restaurants/create');
      }
      return;
    }
    if (['setup', 'general', 'dispatchers', 'branding', 'stripe', 'billing', 'sms', 'people'].includes(action)) {
      onSwitchTab?.(action);
    }
  };

  const actionLabel = (item) => {
    return ACTION_LABELS[item.action] || 'Review';
  };

  const markGoLive = () => {
    setMarking(true);
    markPlatformGoLive(org.id)
      .then((res) => {
        message.success('Marked ready for go-live');
        onMarkedGoLive?.(res.readiness);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setMarking(false));
  };

  const sectionOrder = ['A', 'B', 'C', 'D', 'E', 'F'];
  const orderedSections = sectionOrder
    .map((key) => sections[key])
    .filter(Boolean);

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <Card {...sectionCardProps}>
        <Flex justify="space-between" align="center" gap={12} wrap="wrap">
          <Space wrap>
            {r.go_live_ready ? (
              <Tag color="success">Go-live ready</Tag>
            ) : (
              <Tag color="warning">
                {requiredItemsLabel((r.missing || []).length)}
              </Tag>
            )}
            {org.updated_at && (
              <Tag color="blue">
                Last updated
                {' '}
                {formatGoLiveAt(org.updated_at)}
              </Tag>
            )}
          </Space>
          <Space wrap>
            <Button onClick={onOpenConsole}>Open in console</Button>
            <GoLiveAction
              markedAt={r.go_live_marked_at}
              ready={Boolean(r.go_live_ready)}
              missingCount={(r.missing || []).length}
              marking={marking}
              onMark={markGoLive}
            />
          </Space>
        </Flex>
      </Card>

      <TabNote title="Go-live checklist">
        <span>
          Organization and billing items are completed on the tabs above.
          Location, restaurant, and menu items open the console with this client already selected.
          {org.is_catering
            ? ' Catering clients start with default global menus; their web address is set up under Infra.'
            : ' Standard clients usually need an Expedite dispatcher and a delivery fee.'}
          {' '}
          Stripe Connect uses the restaurant owner email at account creation — choose it carefully.
        </span>
      </TabNote>

      {orderedSections.length === 0 ? (
        <Card {...sectionCardProps} title="Readiness">
          <Text type="secondary">Reload the page to load the expanded checklist.</Text>
        </Card>
      ) : orderedSections.map((section) => {
        const blockers = (section.items || []).filter((item) => item.required && !item.ready);
        const additional = (section.items || []).filter((item) => !item.required && !item.ready);
        const hasBody = blockers.length > 0 || additional.length > 0;

        const renderGroup = (group, isAdditional = false) => (
          <Flex
            key={group.key}
            justify="space-between"
            align={group.items.length > 1 ? 'flex-start' : 'center'}
            gap={12}
            wrap="wrap"
            style={{
              padding: isAdditional ? '6px 0' : '10px 12px',
              border: isAdditional ? 0 : '1px solid #ffe58f',
              borderRadius: isAdditional ? 0 : 6,
              background: isAdditional ? 'transparent' : '#fffbe6',
            }}
          >
            <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 240 }}>
              {group.items.map((item) => (
                <Space key={item.key} size={8} align="start">
                  {!isAdditional && <ExclamationCircleFilled style={{ color: '#d89614' }} />}
                  <Text strong={!isAdditional}>{item.label}</Text>
                  {isAdditional && item.advisory && (
                    <Text type="secondary">Recommended</Text>
                  )}
                </Space>
              ))}
            </Space>
            {group.hasAction && (
              <Button onClick={() => handleAction(group.items[0])}>
                {group.buttonLabel}
              </Button>
            )}
          </Flex>
        );

        return (
          <Card
            key={section.key}
            {...sectionCardProps}
            styles={{
              header: hasBody ? undefined : { paddingBottom: 16 },
              body: hasBody ? sectionCardProps.styles.body : { display: 'none', padding: 0 },
            }}
            title={(
              <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                <Space size={8}>
                  {section.ready ? (
                    <CheckCircleFilled style={{ color: '#52c41a' }} />
                  ) : (
                    <ExclamationCircleFilled style={{ color: '#d89614' }} />
                  )}
                  <span>{section.label}</span>
                </Space>
                {!section.ready && (
                  <Tag color="warning">
                    {requiredItemsLabel(section.missing_count)}
                  </Tag>
                )}
              </Flex>
            )}
          >
            {blockers.length > 0 && (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {groupByDestination(blockers).map((group) => renderGroup(group))}
              </Space>
            )}
            {additional.length > 0 && (
              <Collapse
                ghost
                size="small"
                style={{ marginTop: blockers.length > 0 ? 8 : 0 }}
                items={[{
                  key: 'additional',
                  label: `Additional setup (${additional.length})`,
                  children: (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {groupByDestination(additional).map((group) => renderGroup(group, true))}
                    </Space>
                  ),
                }]}
              />
            )}
          </Card>
        );
      })}

      {r.connect_email_warning && (
        <TabNote type="warning" title="Connect email">{r.connect_email_warning}</TabNote>
      )}
    </Space>
  );
}

function GeneralTab({ org, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      name: org.name,
      website: org.website,
      currency: org.currency,
      omni_merchant_support_phone: org.omni_merchant_support_phone || '',
      is_catering: org.is_catering,
      is_active: org.is_active,
      is_suspended: org.is_suspended,
      should_send_sms: org.should_send_sms,
      should_send_quote_sms: org.should_send_quote_sms,
      tip_options: org.tip_options,
      tip_default_value: org.tip_default_value,
      tip_text: org.tip_text,
      enable_kitchen_tip: Boolean(org.enable_kitchen_tip),
      edit_order_time_window_minutes: org.edit_order_time_window_minutes,
      payment_service_fee_rate: org.payment_service_fee_rate,
      payment_service_fixed_fee: org.payment_service_fixed_fee,
      omnicart_percentage: flattenCommission(org.omnicart_commission).percentage,
      omnicart_fixed: flattenCommission(org.omnicart_commission).fixed,
    });
  }, [org, form]);

  const onCateringChange = (checked) => {
    if (checked === org.is_catering) return;
    const defaults = checked
      ? {
        tip_options: '10,15,20,25',
        tip_default_value: 15,
        tip_text: 'Add a tip for your driver / runner',
      }
      : {
        tip_options: '5,10,15,20',
        tip_default_value: 10,
        tip_text: 'Add a tip for your driver',
      };
    Modal.confirm({
      title: `Apply recommended ${checked ? 'catering' : 'standard'} tip defaults?`,
      content: checked
        ? 'This sets tip options to 10, 15, 20, 25% and the default tip to 15%.'
        : 'This sets tip options to 5, 10, 15, 20% and the default tip to 10%.',
      okText: 'Apply defaults',
      cancelText: 'Keep current tips',
      onOk: () => form.setFieldsValue(defaults),
    });
  };

  const onFinish = (values) => {
    const payload = { ...values };
    payload.omnicart_commission = {
      percentage: payload.omnicart_percentage ?? 0,
      fixed_cents: Math.round(Number(payload.omnicart_fixed || 0) * 100),
    };
    delete payload.omnicart_percentage;
    delete payload.omnicart_fixed;
    payload.omni_merchant_support_phone = toOmniSupportPhone(
      payload.omni_merchant_support_phone,
    );
    if (!payload.omni_merchant_support_phone) {
      delete payload.omni_merchant_support_phone;
    }
    setSaving(true);
    updatePlatformOrganization(org.id, payload)
      .then((updated) => {
        message.success('Saved');
        onSaved(updated);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <TabNote title="Status toggles">
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          <li>
            <strong>Active</strong>
            {' '}
            — leave on for normal use. Turning it off takes every restaurant under this client offline.
          </li>
          <li>
            <strong>Suspended</strong>
            {' '}
            — billing hold. Turning it off clears the hold flag only; unpaid invoices can cause billing to suspend the client again.
          </li>
          <li>
            <strong>*</strong>
            {' '}
            — required before this client can go live. You can still save without them, but the
            go-live checklist stays yellow.
          </li>
        </ul>
      </TabNote>
      {/* autoComplete off keeps Chrome from filling saved admin logins into the API fields. */}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className={platformFormClassName}
      >
        <Space direction="vertical" size={sectionStackGap} style={{ width: '100%' }}>
          <Card {...sectionCardProps} title="Identity">
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={14}>
                <Form.Item name="name" label="Name" rules={[{ required: true }]} style={formItemStyle}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item name="currency" label="Currency" {...goLiveMarkProps} style={formItemStyle}>
                  <Input maxLength={3} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="website" label={optionalLabel('Website')} style={formItemStyle}>
                  <Input placeholder="https://" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="omni_merchant_support_phone"
                  label="POS / Omni customer-facing phone"
                  rules={[
                    { required: true, message: 'Enter the POS / Omni customer-facing phone' },
                    omniSupportPhoneRule,
                  ]}
                  style={{ marginBottom: 0 }}
                  extra="Used as the customer number by integrations that send customer messages. Restaurants may also call this number."
                >
                  <Input placeholder="1234567890" maxLength={32} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card {...sectionCardProps} title="Status">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="is_catering"
                  label="Catering"
                  {...switchFieldProps}
                  style={{ marginBottom: 0 }}
                >
                  <Switch size="small" onChange={onCateringChange} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="is_active"
                  label="Active"
                  {...switchFieldProps}
                  style={{ marginBottom: 0 }}
                  tooltip={formTooltip(
                    'Active',
                    'Off = all restaurants under this client go offline',
                  )}
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="is_suspended"
                  label="Suspended"
                  {...switchFieldProps}
                  style={{ marginBottom: 0 }}
                  tooltip={formTooltip('Suspended', tipLines(
                    'Billing hold.',
                    'Clearing this does not erase overdue invoices.',
                  ))}
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="should_send_sms"
                  label="Send SMS"
                  {...switchFieldProps}
                  style={{ marginBottom: 0 }}
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="should_send_quote_sms"
                  label="Quote SMS"
                  {...switchFieldProps}
                  style={{ marginBottom: 0 }}
                >
                  <Switch size="small" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            {...sectionCardProps}
            title="Tips and order editing"
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Tip defaults and how long orders remain editable. Dispatcher credentials live on the
              Dispatchers tab; default delivery type is set in the client console.
            </Text>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="tip_options"
                  label="Tip percentages"
                  {...goLiveMarkProps}
                  style={formItemStyle}
                  extra="Enter percentages separated by commas, for example 10, 15, 20, 25."
                >
                  <Input placeholder="10, 15, 20, 25" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="tip_default_value" label="Default tip %" {...goLiveMarkProps} style={formItemStyle}>
                  <InputNumber style={{ width: '100%' }} min={0} max={100} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="tip_text" label={optionalLabel('Tip description')} style={formItemStyle}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="enable_kitchen_tip"
                  label="Enable Kitchen Tip"
                  valuePropName="checked"
                  style={formItemStyle}
                  extra="Delivery checkout only. Off by default; brands/locations can override. When on, kitchen tip starts at $0."
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="edit_order_time_window_minutes"
                  label="Order edit window"
                  {...goLiveMarkProps}
                  style={{ marginBottom: 0 }}
                  extra="How long an order can be edited after delivery."
                >
                  <InputNumber style={{ width: 200 }} min={1} addonAfter="minutes" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card {...sectionCardProps} title="Commercial rates">
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="payment_service_fee_rate"
                  label="Payment processing rate"
                  style={formItemStyle}
                  extra="Stripe card rate. Default is 0.029 (2.9%)."
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.001} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="payment_service_fixed_fee"
                  label="Payment processing fixed fee"
                  style={formItemStyle}
                  extra="Stripe fixed fee per charge. Default is $0.31."
                >
                  <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="$" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Per-order Nibble commission"
                  extra="Applied on each order, separate from the monthly OmniMerchant invoice commission. Enter a percentage, a fixed amount, or both."
                  style={{ marginBottom: 0 }}
                >
                  <CommissionInputs
                    percentageName="omnicart_percentage"
                    fixedName="omnicart_fixed"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
        </Space>
      </Form>
    </Space>
  );
}

function SetupTab({ org, onSaved }) {
  const [feesLoading, setFeesLoading] = useState(false);
  const [editingFees, setEditingFees] = useState(false);
  const [feesForm] = Form.useForm();
  const r = org.readiness || {};
  const fees = org.delivery_fees || {};

  const submitFees = (payload, successMessage) => {
    setFeesLoading(true);
    return bootstrapPlatformDeliveryFees(org.id, payload)
      .then((res) => {
        const changed = (res.created || []).length || (res.updated || []).length;
        message[changed ? 'success' : 'info'](
          changed ? successMessage : 'Delivery fees are already up to date',
        );
        setEditingFees(false);
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setFeesLoading(false));
  };

  const runFees = (force = false) => submitFees(
    { force },
    force ? 'Delivery fees reset to defaults' : 'Delivery fees set up',
  );

  const saveFees = (values) => {
    const delivery = {
      constant_amount: values.constant_amount,
      additional_mile_amount: values.additional_mile_amount,
      max_constant_distance: values.max_constant_distance,
    };
    const payload = { delivery_formula: delivery };
    if (values.self_constant_amount != null) {
      payload.selfdelivery_formula = {
        constant_amount: values.self_constant_amount,
        additional_mile_amount: values.self_additional_mile_amount,
        max_constant_distance: values.self_max_constant_distance,
      };
    }
    return submitFees(payload, 'Delivery fees updated');
  };

  const startEditingFees = () => {
    const delivery = fees.delivery_formula || {};
    const self = fees.selfdelivery_formula || {};
    feesForm.setFieldsValue({
      constant_amount: delivery.constant_amount,
      additional_mile_amount: delivery.additional_mile_amount,
      max_constant_distance: delivery.max_constant_distance,
      self_constant_amount: self.constant_amount,
      self_additional_mile_amount: self.additional_mile_amount,
      self_max_constant_distance: self.max_constant_distance,
    });
    setEditingFees(true);
  };

  const selfDeliveryRequired = fees.selfdelivery_required
    ?? (org.default_delivery_type === 'self_delivery');
  const feesFullyAttached = r.has_delivery_fee_formula
    && (!selfDeliveryRequired || r.has_selfdelivery_fee_formula);
  const defaultFees = feeSentence(fees.defaults);
  const feesMatchDefaults = formulaMatchesDefaults(fees.delivery_formula, fees.defaults)
    && (!selfDeliveryRequired || formulaMatchesDefaults(fees.selfdelivery_formula, fees.defaults));

  const confirmRecreateFees = () => {
    Modal.confirm({
      title: 'Reset delivery fees to defaults?',
      content: defaultFees
        ? `This client will use ${defaultFees}. The current fees stop being used, and any custom amounts are lost.`
        : 'This client goes back to Platform default fees. Any custom amounts are lost.',
      okText: 'Reset to defaults',
      onOk: () => runFees(true),
    });
  };

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <TabNote title="Delivery fees">
        Set what this client charges customers for delivery. SMS senders live on the SMS tab and
        the billing plan lives on the Billing tab.
      </TabNote>

      <Card
        {...sectionCardProps}
        title="Delivery fees"
        extra={<StatusTag ready={feesFullyAttached} />}
      >
        {feesFullyAttached && editingFees && (
          <Form form={feesForm} layout="vertical" onFinish={saveFees} className={platformFormClassName}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Customer delivery fee</Text>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8}>
                <Form.Item name="constant_amount" label="Base fee" rules={[{ required: true }]} style={formItemStyle}>
                  <InputNumber style={{ width: '100%' }} min={0} step={0.5} prefix="$" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="max_constant_distance"
                  label="Covers up to (mi)"
                  rules={[{ required: true }]}
                  style={formItemStyle}
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={9.99} step={0.5} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="additional_mile_amount"
                  label="Per extra mile"
                  rules={[{ required: true }]}
                  style={formItemStyle}
                >
                  <InputNumber style={{ width: '100%' }} min={0} step={0.25} prefix="$" />
                </Form.Item>
              </Col>
            </Row>
            {selfDeliveryRequired && (
              <>
                <Text strong style={{ display: 'block', margin: '4px 0 8px' }}>Self-delivery fee</Text>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="self_constant_amount" label="Base fee" rules={[{ required: true }]} style={formItemStyle}>
                      <InputNumber style={{ width: '100%' }} min={0} step={0.5} prefix="$" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="self_max_constant_distance"
                      label="Covers up to (mi)"
                      rules={[{ required: true }]}
                      style={formItemStyle}
                    >
                      <InputNumber style={{ width: '100%' }} min={0} max={9.99} step={0.5} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="self_additional_mile_amount"
                      label="Per extra mile"
                      rules={[{ required: true }]}
                      style={formItemStyle}
                    >
                      <InputNumber style={{ width: '100%' }} min={0} step={0.25} prefix="$" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            <Space wrap>
              <Button type="primary" htmlType="submit" loading={feesLoading}>Save fees</Button>
              <Button onClick={() => setEditingFees(false)}>Cancel</Button>
            </Space>
          </Form>
        )}
        {feesFullyAttached && !editingFees && (
          <>
            <KeyValueTable
              rows={[
                {
                  key: 'customer',
                  label: 'Customer delivery fee',
                  value: feeSentence(fees.delivery_formula) || '—',
                },
                ...(selfDeliveryRequired
                  ? [{
                    key: 'self',
                    label: 'Self-delivery fee',
                    value: feeSentence(fees.selfdelivery_formula) || '—',
                  }]
                  : []),
              ]}
            />
            <Space wrap>
              <Button type="primary" onClick={startEditingFees}>
                Edit fees
              </Button>
              {!feesMatchDefaults && (
                <Button loading={feesLoading} onClick={confirmRecreateFees}>
                  Reset to defaults
                </Button>
              )}
            </Space>
          </>
        )}
        {!feesFullyAttached && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {selfDeliveryRequired
                ? 'Sets the customer delivery fee and the self-delivery fee this client charges.'
                : 'Sets the customer delivery fee this client charges.'}
              {defaultFees ? ` Defaults to ${defaultFees}.` : ''}
            </Text>
            <Button type="primary" loading={feesLoading} onClick={() => runFees(false)}>
              Set up delivery fees
            </Button>
          </>
        )}
      </Card>
    </Space>
  );
}

function BrandingTab({ org, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const logoPreviewGeneration = useRef(0);
  const branding = org.branding || {};
  const primaryColor = Form.useWatch('primary_color', form);
  const logoUrl = Form.useWatch('logo_url', form);
  const normalizedPrimaryColor = /^#?[0-9A-Fa-f]{6}$/.test(primaryColor || '')
    ? `#${primaryColor.replace('#', '')}`
    : null;
  const savedLogoUrl = branding.logo_url || '';
  const enteredLogoUrl = logoUrl === undefined ? savedLogoUrl : (logoUrl || '').trim();
  const previewLogoUrl = localPreviewUrl || (!logoFile && branding.logo_preview_url) || '';
  // Only images on Nibble storage clear the marketplace and admin image CSPs, so an
  // edited or legacy external URL has nothing to show until a save re-hosts it.
  // A pending file hides the stored preview so we never show the previous client's logo.
  const showLogoPreview = Boolean(localPreviewUrl)
    || (!logoFile
      && Boolean(branding.logo_preview_url)
      && branding.logo_is_hosted
      && enteredLogoUrl === savedLogoUrl);
  const [logoPreviewFailed, setLogoPreviewFailed] = useState(false);
  useEffect(() => {
    setLogoPreviewFailed(false);
  }, [previewLogoUrl]);

  useEffect(() => {
    form.setFieldsValue({
      name: branding.name || org.name,
      logo_url: branding.logo_url || '',
      email: branding.email || '',
      phone: phoneDigitsForInput(branding.phone),
      primary_color: branding.primary_color || '',
      default_lat: branding.default_lat,
      default_long: branding.default_long,
    });
  }, [org, branding, form]);

  // A data URL, not a blob URL: the admin CSP allows `data:` images everywhere,
  // so the pre-save preview works on staging and production without a CSP change.
  const setPendingLogo = (file) => {
    const generation = logoPreviewGeneration.current + 1;
    logoPreviewGeneration.current = generation;
    setLocalPreviewUrl('');
    setLogoFile(file);
    setLogoPreviewFailed(false);
    if (!file || !shouldPreviewLogoFileInMemory(file)) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (generation !== logoPreviewGeneration.current) return;
      setLocalPreviewUrl(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsDataURL(file);
  };

  const onFinish = (values) => {
    setSaving(true);
    updatePlatformBranding(
      org.id,
      {
        ...values,
        phone: toE164Phone(values.phone) || '',
      },
      logoFile || undefined,
    )
      .then((updated) => {
        message.success('Branding saved');
        setPendingLogo(null);
        onSaved(updated);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <TabNote title="Branding">
        Set the logo, support contact, brand color, and map center. Fields marked
        {' '}
        <Text strong>*</Text>
        {' '}
        are required before this client can go live, so the go-live checklist stays yellow until
        they are filled. The map center is where the console map opens when drawing a
        location&apos;s delivery area. Marketplace landing content starts from defaults.
      </TabNote>
      <Card {...sectionCardProps} title="Brand & map">
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          size="small"
          colon={false}
          labelStyle={descriptionsLabelStyle}
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="Schema">
            <StatusTag ready={branding.is_schema_valid} />
          </Descriptions.Item>
          <Descriptions.Item label="Map center">
            <StatusTag ready={branding.has_map_defaults} />
          </Descriptions.Item>
        </Descriptions>
        <Form form={form} layout="vertical" onFinish={onFinish} className={platformFormClassName}>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={14}>
              <Form.Item
                name="name"
                label={optionalLabel('Display name')}
                style={formItemStyle}
                extra="Blank uses the organization name."
              >
                <Input placeholder={org.name} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item
                label="Primary color"
                {...goLiveMarkProps}
                style={formItemStyle}
                extra="The client's own brand color."
              >
                <Space.Compact style={{ width: '100%' }}>
                  {normalizedPrimaryColor ? (
                    <ColorPicker
                      value={normalizedPrimaryColor}
                      disabledAlpha
                      onChangeComplete={(color) => {
                        form.setFieldValue('primary_color', color.toHexString().toUpperCase());
                      }}
                    />
                  ) : (
                    <span
                      aria-label="No color selected"
                      style={{
                        width: 32,
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px 0 0 6px',
                        background: 'linear-gradient(135deg, transparent 45%, #bfbfbf 46%, #bfbfbf 54%, transparent 55%)',
                      }}
                    />
                  )}
                  <Form.Item
                    name="primary_color"
                    noStyle
                    rules={[
                      { required: true, message: GO_LIVE_REQUIRED },
                      {
                        pattern: /^#?[0-9A-Fa-f]{6}$/,
                        message: 'Use #RRGGBB',
                      },
                    ]}
                  >
                    <Input placeholder="#RRGGBB" addonBefore="hex" />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Logo"
                style={formItemStyle}
                extra="A logo is required for go-live. Upload a file (PNG, JPEG, WebP, or GIF, up to 5 MB) or paste a full https:// URL. Saving copies the image onto Nibble storage, since client storefronts only load images we host. If you upload a file and also paste a URL, the file is the one that is saved."
              >
                <Upload.Dragger
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                  maxCount={1}
                  multiple={false}
                  fileList={logoFile ? [{ uid: 'pending-logo', name: logoFile.name, status: 'done' }] : []}
                  beforeUpload={(file) => {
                    const reason = logoFileRejectReason(file);
                    if (reason) {
                      message.error(reason);
                      return Upload.LIST_IGNORE;
                    }
                    setPendingLogo(file);
                    return false;
                  }}
                  onRemove={() => {
                    setPendingLogo(null);
                    return true;
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag a logo file here</p>
                  <p className="ant-upload-hint">PNG, JPEG, WebP, or GIF. Max 5 MB.</p>
                </Upload.Dragger>
                <Form.Item
                  name="logo_url"
                  rules={[{
                    validator: (_, value) => (
                      logoFile || isAcceptableLogoUrl(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error('Enter a full https:// URL or upload a file'))
                    ),
                  }]}
                  style={{ marginTop: 12, marginBottom: (enteredLogoUrl || logoFile) ? 12 : 0 }}
                >
                  <Input placeholder="or paste https://..." />
                </Form.Item>
                {(enteredLogoUrl || logoFile) && (
                  <div>
                    <Text type="secondary">Logo preview</Text>
                    <div style={{ marginTop: 6 }}>
                      {!showLogoPreview && (
                        <Text type="secondary">
                          {logoFile && !shouldPreviewLogoFileInMemory(logoFile)
                            ? 'This file is too large to preview in the browser. Save to copy it onto Nibble storage, then it appears here.'
                            : 'Save to copy this image onto Nibble storage, then it appears here.'}
                        </Text>
                      )}
                      {showLogoPreview && logoPreviewFailed && (
                        <Text type="danger">
                          {logoFile
                            ? 'This file could not be previewed. You can still save it.'
                            : 'The stored logo did not load. Upload the file again or paste the source URL to re-copy it.'}
                        </Text>
                      )}
                      {showLogoPreview && !logoPreviewFailed && (
                        <img
                          src={previewLogoUrl}
                          alt={`${branding.name || org.name} logo`}
                          width={180}
                          height={100}
                          onError={() => setLogoPreviewFailed(true)}
                          style={{
                            objectFit: 'contain',
                            padding: 8,
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            background: '#fff',
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Support email"
                rules={[{ required: true, message: GO_LIVE_REQUIRED }, { type: 'email' }]}
                style={formItemStyle}
              >
                <Input placeholder="support@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label="Support phone"
                {...goLiveMarkProps}
                rules={[requiredPhoneRule]}
                style={formItemStyle}
              >
                <PhoneInput
                  country="us"
                  buttonClass="phone-country"
                  inputClass="input"
                  inputProps={{ name: 'phone' }}
                  inputStyle={{ width: '100%', height: 32 }}
                  placeholder="+1 202 555 0123"
                  preferredCountries={['us', 'ca']}
                  enableSearch
                  regions={['america', 'europe']}
                  countryCodeEditable={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="default_lat"
                label="Map center latitude"
                rules={[{ required: true, message: 'Required for Location map' }]}
                style={formItemStyle}
              >
                <InputNumber style={{ width: '100%' }} step={0.000001} placeholder="47.6062" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="default_long"
                label="Map center longitude"
                rules={[{ required: true, message: 'Required for Location map' }]}
                style={formItemStyle}
              >
                <InputNumber style={{ width: '100%' }} step={0.000001} placeholder="-122.3321" />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={saving}>Save branding</Button>
        </Form>
      </Card>
    </Space>
  );
}

function StripeTab({ org, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const onFinish = (values) => {
    const payload = {};
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        payload[key] = value;
      }
    });
    if (Object.keys(payload).length === 0) {
      message.info('No secret fields to update');
      return;
    }
    setSaving(true);
    updatePlatformOrganization(org.id, payload)
      .then((updated) => {
        message.success('Stripe secrets updated');
        form.resetFields();
        onSaved(updated);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <Card {...sectionCardProps} title="Current secrets">
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          size="small"
          colon={false}
          labelStyle={descriptionsLabelStyle}
        >
          <Descriptions.Item label="Public key"><SecretMeta meta={org.stripe_api_public_key_meta} /></Descriptions.Item>
          <Descriptions.Item label="Private key"><SecretMeta meta={org.stripe_api_private_key_meta} /></Descriptions.Item>
          <Descriptions.Item label="Payment webhook"><SecretMeta meta={org.payment_webhook_secret_meta} /></Descriptions.Item>
          <Descriptions.Item label="Payment failed"><SecretMeta meta={org.payment_failed_webhook_secret_meta} /></Descriptions.Item>
          <Descriptions.Item label="Stripe webhook"><SecretMeta meta={org.stripe_webhook_secret_meta} /></Descriptions.Item>
          <Descriptions.Item label="Connect webhook"><SecretMeta meta={org.stripe_connect_webhook_secret_meta} /></Descriptions.Item>
        </Descriptions>
      </Card>

      <Card {...sectionCardProps} title="Rotate secrets">
        <TabNote title="Rotating keys" style={{ marginBottom: 16 }}>
          Leave a field blank to keep the current value. Saving a blank field never erases an
          existing secret.
        </TabNote>
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" className={platformFormClassName}>
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item name="stripe_api_public_key" label="Public key" style={formItemStyle}>
                <Input placeholder="Leave blank to keep current" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="stripe_api_private_key" label="Private key" style={formItemStyle}>
                <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="payment_webhook_secret" label="Payment webhook" style={formItemStyle}>
                <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="payment_failed_webhook_secret" label="Payment failed webhook" style={formItemStyle}>
                <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="stripe_webhook_secret" label="Stripe webhook" style={formItemStyle}>
                <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="stripe_connect_webhook_secret" label="Connect webhook" style={formItemStyle}>
                <Input.Password placeholder="Leave blank to keep current" autoComplete="new-password" />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={saving}>Update secrets</Button>
        </Form>
      </Card>
    </Space>
  );
}

function BillingPlanForm({
  form, plan, saving, onFinish, onCancel,
}) {
  return (
    <>
      <TabNote title="Commercial terms" style={{ marginBottom: 16 }}>
        Set monthly pricing and the commissions this client is billed. Zero means that fee is
        not charged, and a client with all-zero pricing needs no card. Restaurant menu markup
        stays on the merchant form after the restaurant exists.
      </TabNote>
      <Form form={form} layout="vertical" onFinish={onFinish} className={platformFormClassName}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="base_price" label="Base price" rules={[{ required: !plan }]} style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="$" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="catering_price" label={optionalLabel('Catering price')} style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="$" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="base_number_of_orders" label="Included orders" rules={[{ required: !plan }]} style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="price_per_extra_order" label="Price per extra order" rules={[{ required: !plan }]} style={formItemStyle}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="$" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="billing_cycle_days"
              label={optionalLabel('Billing cycle (days)')}
              style={formItemStyle}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="invoices_deadline_days"
              label={optionalLabel('Invoice payment deadline')}
              style={formItemStyle}
              extra="Number of days the client has to pay an invoice."
            >
              <InputNumber style={{ width: '100%' }} min={1} addonAfter="days" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="map_price_per_address"
              label={optionalLabel('Map price per address')}
              style={formItemStyle}
              extra="Charged for each delivery address included in a map-based order."
            >
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="$" />
            </Form.Item>
          </Col>
          {CommissionFields()}
        </Row>
        <Space wrap>
          <Button type="primary" htmlType="submit" loading={saving}>Save plan</Button>
          {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        </Space>
      </Form>
    </>
  );
}

function BillingTab({ org, onSaved }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presetLoading, setPresetLoading] = useState(false);
  // A saved plan opens collapsed; only a client without a plan starts in the form.
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  const [plan, setPlan] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pausing, setPausing] = useState(false);

  const setPlanEditing = (next) => {
    editingRef.current = next;
    setEditing(next);
  };

  const loadPlan = useCallback(() => {
    setLoading(true);
    fetchPlatformBillingPlan(org.id)
      .then((data) => {
        setPlan(data);
        if (!editingRef.current) {
          form.setFieldsValue(flattenPlanCommissions(data));
        }
      })
      .catch(() => {
        setPlan(null);
        if (!editingRef.current) {
          form.resetFields();
        }
      })
      .finally(() => setLoading(false));
  }, [org.id, form]);

  const loadCards = useCallback(() => {
    setCardsLoading(true);
    fetchOrgPaymentMethods(org.id)
      .then((res) => setCards(res.data || res || []))
      .catch((e) => captureException(e))
      .finally(() => setCardsLoading(false));
  }, [org.id]);

  // Reload the plan when it is created/removed, not when a card is added — that used
  // to collapse the editor and drop unsaved commercial terms.
  useEffect(() => {
    loadPlan();
  }, [loadPlan, org.readiness?.has_billing_plan]);

  useEffect(() => {
    loadCards();
  }, [loadCards, org.readiness?.has_payment_method]);

  const onFinish = (values) => {
    setSaving(true);
    const payload = collectPlanCommissions(values);
    const req = plan ? patchPlatformBillingPlan(org.id, payload) : upsertPlatformBillingPlan(org.id, payload);
    req
      .then((data) => {
        setPlan(data);
        setPlanEditing(false);
        form.setFieldsValue(flattenPlanCommissions(data));
        message.success('Billing plan saved');
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setSaving(false));
  };

  const pausePlan = () => {
    setPausing(true);
    return pausePlatformBillingPlan(org.id)
      .then((data) => {
        setPlan(data);
        setPlanEditing(false);
        message.success('Billing plan paused');
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setPausing(false));
  };

  const resumePlan = () => {
    setPausing(true);
    return resumePlatformBillingPlan(org.id)
      .then((data) => {
        setPlan(data);
        message.success('Billing plan resumed');
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setPausing(false));
  };

  const deletePlan = () => deletePlatformBillingPlan(org.id)
      .then(() => {
        setPlan(null);
        setPlanEditing(false);
        form.resetFields();
        message.success('Billing plan deleted');
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      });

  const confirmPause = () => {
    const resumeStaysOff = org.is_active === false && Boolean(org.is_suspended);
    Modal.confirm({
      title: 'Pause billing plan?',
      okText: 'Pause plan',
      content: (
        <Space direction="vertical" size={8}>
          <Text>
            {resumeStaysOff
              ? 'The plan is kept. Resume stays off while this client is inactive and suspended.'
              : 'The plan is kept and can be resumed at any time.'}
          </Text>
          <Text>
            The current cycle is charged in full, with no proration, on the next invoice run after you resume.
          </Text>
          <Text>Cycles that start while paused aren&apos;t billed.</Text>
          <Text>Existing invoices can still be collected.</Text>
        </Space>
      ),
      onOk: pausePlan,
    });
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: 'Delete billing plan?',
      okText: 'Delete plan',
      okButtonProps: { danger: true },
      content: (
        <Space direction="vertical" size={8}>
          <Text>The plan is removed from this client.</Text>
          <Text>The current cycle won&apos;t be invoiced.</Text>
          <Text>This client can&apos;t go live until a new plan is added.</Text>
          <Text>Existing invoices can still be collected.</Text>
        </Space>
      ),
      onOk: deletePlan,
    });
  };

  const applyNoFeePlan = () => {
    Modal.confirm({
      title: 'Apply no-fee billing plan?',
      content: plan
        ? 'This replaces the current monthly prices and billed commissions with $0.'
        : 'This creates a billing plan with $0 monthly prices and $0 billed commissions.',
      okText: 'Apply no-fee plan',
      onOk: () => {
        setPresetLoading(true);
        return applyPlatformBillingPreset(org.id, { preset: 'zero_monthly' })
          .then(() => {
            message.success('No-fee plan applied — no card needed');
            setPlanEditing(false);
            loadPlan();
            onSaved();
          })
          .catch((e) => {
            captureException(e);
            message.error(getHttpErrorMessage(e));
          })
          .finally(() => setPresetLoading(false));
      },
    });
  };

  const afterCardChange = () => Promise.all([
    loadCards(),
    // Refresh org readiness so Overview go-live / payment-method checklist updates
    // without requiring a full page reload.
    Promise.resolve(onSaved()),
  ]);

  const removeCard = (cardId) => {
    Modal.confirm({
      title: 'Remove payment method?',
      onOk: () => deleteOrgPaymentMethod(org.id, cardId)
        .then(afterCardChange)
        .catch((e) => {
          captureException(e);
          message.error(getHttpErrorMessage(e));
        }),
    });
  };

  const startEditing = () => {
    form.setFieldsValue(flattenPlanCommissions(plan));
    setPlanEditing(true);
  };

  const cardRequired = planRequiresCard(plan);
  const hasCards = Array.isArray(cards) && cards.length > 0;
  const showForm = !plan || editing;
  const orgBillingHeld = org.is_active === false && Boolean(org.is_suspended);
  const canResume = Boolean(plan?.is_paused) && !orgBillingHeld;
  const planMenuItems = [
    !plan?.is_paused && { key: 'pause', label: 'Pause plan', onClick: confirmPause },
    { key: 'delete', label: 'Delete plan', danger: true, onClick: confirmDelete },
  ].filter(Boolean);

  if (loading) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <Card
        {...sectionCardProps}
        title={plan ? 'Billing plan' : 'Create billing plan'}
        extra={(
          <Space size={8} wrap>
            {plan?.is_paused ? (
              <Tag color="warning" bordered={false}>Paused</Tag>
            ) : (
              <StatusTag ready={Boolean(plan)} readyLabel="Plan set" pendingLabel="No plan yet" />
            )}
            {showForm && (
              <Button loading={presetLoading} onClick={applyNoFeePlan}>
                Apply no-fee plan
              </Button>
            )}
            {canResume && (
              <Button type="primary" loading={pausing} onClick={resumePlan}>
                Resume billing
              </Button>
            )}
            {plan && !showForm && !canResume && (
              <Button type="primary" onClick={startEditing}>
                Edit plan
              </Button>
            )}
            {plan && !showForm && canResume && (
              <Button onClick={startEditing}>Edit plan</Button>
            )}
            {plan && (
              <Dropdown menu={{ items: planMenuItems }} trigger={['click']} placement="bottomRight">
                <Button aria-label="Billing plan actions" icon={<EllipsisOutlined />} />
              </Dropdown>
            )}
          </Space>
        )}
      >
        {!plan && (
          <TabNote type="warning" title="Billing plan required" style={{ marginBottom: 12 }}>
            This client can&apos;t go live until a billing plan is added.
          </TabNote>
        )}
        {plan?.is_paused && (
          <TabNote type="warning" title="Billing paused" style={{ marginBottom: 12 }}>
            The cycle already underway is charged in full. Later cycles are not billed
            until you resume this plan.
            {orgBillingHeld
              ? ' Resume stays off while this client is inactive and suspended.'
              : ' Invoices that already exist can still be collected.'}
          </TabNote>
        )}
        {orgBillingHeld && (
          <TabNote type="warning" title="New invoices are on hold" style={{ marginBottom: 12 }}>
            This client is inactive and suspended. The cycle already underway is charged
            in full. Later cycles are not billed until the client is active again or no
            longer suspended. Invoices that already exist can still be collected.
          </TabNote>
        )}
        {showForm ? (
          <BillingPlanForm
            form={form}
            plan={plan}
            saving={saving}
            onFinish={onFinish}
            onCancel={plan ? () => setPlanEditing(false) : null}
          />
        ) : (
          <KeyValueTable rows={billingPlanSummaryRows(plan, { isCatering: org.is_catering })} />
        )}
      </Card>

      <Card
        {...sectionCardProps}
        title="Payment methods"
        extra={(
          <Button type="primary" onClick={() => setModalOpen(true)}>
            Add card
          </Button>
        )}
      >
        {cardRequired && !hasCards && (
          <TabNote type="warning" title="Card required" style={{ marginBottom: 12 }}>
            This plan has a monthly price or billed commission, so a card must be on file before
            this client can go live.
          </TabNote>
        )}
        {!cardRequired && plan && (
          <TabNote title="Card optional" style={{ marginBottom: 12 }}>
            Monthly prices and billed commissions are all $0, so this client can go live without a
            card.
          </TabNote>
        )}
        <Table
          rowKey="id"
          size="small"
          loading={cardsLoading}
          dataSource={Array.isArray(cards) ? cards : []}
          pagination={false}
          locale={{ emptyText: 'No payment methods' }}
          columns={[
            {
              title: 'Card',
              render: (_, row) => `${row.card?.brand || 'card'} ···· ${row.card?.last4 || ''}`,
            },
            {
              title: '',
              width: 90,
              align: 'right',
              render: (_, row) => (
                <Button danger type="link" size="small" onClick={() => removeCard(row.id)}>
                  Remove
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <StripePaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        reloadCards={() => afterCardChange()}
        container={StripeContainer}
        containerProps={{
          createIntent: () => createOrgSetupIntent(org.id),
        }}
      />
    </Space>
  );
}

function SmsTab({ org, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const hasSmsReady = Boolean(org.readiness?.has_sms_client);

  const load = useCallback(() => {
    setLoading(true);
    fetchPlatformSmsClients(org.id)
      .then((res) => {
        const list = res.results || res || [];
        // Urgent (4) first — matches go-live expectation of Urgent + High.
        setRows([...list].sort((a, b) => Number(b.priority) - Number(a.priority)));
      })
      .catch((e) => captureException(e))
      .finally(() => setLoading(false));
  }, [org.id]);

  // SMS bootstrap elsewhere updates readiness without remounting this tab.
  const smsRevision = org.readiness?.has_sms_client ? '1' : '0';

  useEffect(() => { load(); }, [load, smsRevision]);

  const bootstrap = () => {
    setBootstrapping(true);
    bootstrapPlatformSms(org.id)
      .then((res) => {
        message.success(`SMS Urgent + High ready (${res.phone_number})`);
        if (res.warning) message.warning(res.warning);
        load();
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setBootstrapping(false));
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ priority: 2, driver: 'twilio', max_retry: 0, should_fallback_after_retries: false });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue(row);
    setModalOpen(true);
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        phone_number: String(values.phone_number || '').trim(),
      };
      const req = editing
        ? updatePlatformSmsClient(org.id, editing.id, payload)
        : createPlatformSmsClient(org.id, payload);
      req
        .then(() => {
          message.success(editing ? 'Updated' : 'Created');
          setModalOpen(false);
          load();
          onSaved();
        })
        .catch((e) => {
          captureException(e);
          message.error(getHttpErrorMessage(e));
        });
    });
  };

  const onDelete = (row) => {
    Modal.confirm({
      title: 'Delete SMS client?',
      onOk: () => deletePlatformSmsClient(org.id, row.id)
        .then(() => {
          load();
          onSaved();
        })
        .catch((e) => {
          captureException(e);
          message.error(getHttpErrorMessage(e));
        }),
    });
  };

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <TabNote title="SMS notifications">
        Go-live needs both an <Text strong>Urgent (4)</Text> and a <Text strong>High (3)</Text> sender.
        Use <Text strong>Set up urgent + high</Text> to create both from the shared Twilio number, or add
        clients manually (long codes and short codes are allowed).
      </TabNote>

      <Card
        {...sectionCardProps}
        title="SMS clients"
        extra={(
          <Space size={8} wrap>
            <StatusTag
              ready={hasSmsReady}
              readyLabel="Urgent + High ready"
              pendingLabel="Urgent + High not set up"
            />
            {!hasSmsReady && (
              <Button loading={bootstrapping} onClick={bootstrap}>
                Set up urgent + high
              </Button>
            )}
            <Button type="primary" onClick={openCreate}>
              Add SMS client
            </Button>
          </Space>
        )}
      >
        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          dataSource={rows}
          pagination={false}
          locale={{ emptyText: 'No SMS clients yet — set up Urgent + High, or add one manually.' }}
          columns={[
            {
              title: 'Phone',
              dataIndex: 'phone_number',
              render: (v) => <Text code>{v || '—'}</Text>,
            },
            {
              title: 'Priority',
              dataIndex: 'priority',
              width: 140,
              render: (v) => {
                const meta = smsPriorityMeta(v);
                return <Tag color={meta.color}>{meta.label}</Tag>;
              },
            },
            {
              title: 'Driver',
              dataIndex: 'driver',
              width: 130,
              render: (v) => smsDriverLabel(v),
            },
            {
              title: 'Max retry',
              dataIndex: 'max_retry',
              width: 100,
              render: (v) => (v == null ? '—' : v),
            },
            {
              title: 'Fallback',
              dataIndex: 'should_fallback_after_retries',
              width: 100,
              render: (v) => (v ? 'Yes' : 'No'),
            },
            {
              title: '',
              width: 140,
              align: 'right',
              render: (_, row) => (
                <Space size={4}>
                  <Button type="link" size="small" onClick={() => openEdit(row)}>Edit</Button>
                  <Button type="link" size="small" danger onClick={() => onDelete(row)}>Delete</Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? 'Edit SMS client' : 'Add SMS client'}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editing ? 'Save' : 'Add'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className={platformFormClassName} style={{ marginTop: 8 }}>
          <Form.Item
            name="phone_number"
            label="Phone number"
            rules={[{ required: true, message: 'Phone number is required' }]}
            style={formItemStyle}
            extra="Twilio long codes or short codes are both allowed (no country-format enforcement)."
          >
            <Input placeholder="e.g. +12025550123 or 58973" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" rules={[{ required: true }]} style={formItemStyle}>
                <Select options={SMS_PRIORITY_OPTIONS.map(({ value, label }) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="driver" label="Driver" rules={[{ required: true }]} style={formItemStyle}>
                <Select options={[
                  { value: 'twilio', label: 'Twilio' },
                  { value: 'amazon sns', label: 'Amazon SNS' },
                  { value: 'debug', label: 'Debug' },
                ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="max_retry" label="Max retry" style={formItemStyle}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="should_fallback_after_retries"
                valuePropName="checked"
                label="Fallback after retries"
                style={formItemStyle}
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
}

function PeopleTab({ org, onSaved }) {
  const [ownerForm] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [rdsForm] = Form.useForm();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invitingRds, setInvitingRds] = useState(false);
  const { setOrganization } = useContext(AdminContext);
  const navigate = useNavigate();
  const deepLinks = org.readiness?.deep_links || {};
  const connectWarning = org.readiness?.connect_email_warning
    || 'Stripe Connect uses the restaurant owner email at account creation. Choose the final email before onboarding.';

  useEffect(() => {
    // Persist synchronously so {{organizationId}} substitutes cannot race AdminContext.
    // Permission APIs below also receive org.id explicitly as a belt-and-suspenders fix.
    const ctx = { id: org.id, name: org.name };
    persistOrganizationSelection(ctx);
    setOrganization(ctx);
    setLoading(true);
    fetchUsersPermissions({ organizationId: org.id })
      .then((res) => setAdmins(res.results || res.data?.results || []))
      .catch((e) => captureException(e))
      .finally(() => setLoading(false));
  }, [org.id, org.name, setOrganization]);

  const setOwner = (values) => {
    setPlatformOrganizationOwner(org.id, values)
      .then(() => {
        message.success('Owner updated');
        onSaved();
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      });
  };

  const inviteRdsOwner = (values) => {
    setInvitingRds(true);
    invitePlatformRdsOwner(org.id, {
      email: values.email,
      first_name: values.first_name || '',
      last_name: values.last_name || '',
      set_as_owner: values.set_as_owner !== false,
    })
      .then((res) => {
        message.success(res.message || 'RDS Owner invite sent');
        rdsForm.resetFields();
        onSaved();
        return fetchUsersPermissions({ organizationId: org.id });
      })
      .then((res) => setAdmins(res.results || res.data?.results || []))
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setInvitingRds(false));
  };

  const inviteAdmin = (values) => {
    createNewUserPermission({
      organizationId: org.id,
      email: values.email,
      first_name: values.first_name || '',
      last_name: values.last_name || '',
      roles: values.roles?.length ? values.roles : ['RDS Admin'],
      access_all_parents: true,
      parents: [],
    })
      .then((res) => {
        message.success(res.message || 'Invite sent');
        inviteForm.resetFields();
        return fetchUsersPermissions({ organizationId: org.id });
      })
      .then((res) => setAdmins(res.results || res.data?.results || []))
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      });
  };

  return (
    <Space direction="vertical" size={sectionStackGap} style={tabContentStyle}>
      <TabNote type="warning" title="Connect email">{connectWarning}</TabNote>

      <Card {...sectionCardProps} title="Invite RDS Owner">
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Creates or invites a user with the
          {' '}
          <Text strong>RDS Owner</Text>
          {' '}
          role and access to all
          locations. Sets them as the organization owner when none is set yet. They stay pending
          until they open the invite email and create their account.
        </Text>
        <Form
          form={rdsForm}
          layout="vertical"
          onFinish={inviteRdsOwner}
          initialValues={{ set_as_owner: true }}
          className={platformFormClassName}
        >
          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', required: true }]} style={formItemStyle}>
                <Input placeholder="owner@client.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item
                name="set_as_owner"
                label="Also set as organization owner"
                {...switchFieldProps}
                style={formItemStyle}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="first_name" label={optionalLabel('First name')} style={formItemStyle}>
                <Input placeholder="Jane" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="last_name" label={optionalLabel('Last name')} style={formItemStyle}>
                <Input placeholder="Doe" />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={invitingRds}>Invite RDS Owner</Button>
        </Form>
      </Card>

      <Card {...sectionCardProps} title="Owner (existing user)">
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Current: {org.owner_email || 'None'} (must already exist)
        </Text>
        <Form form={ownerForm} layout="inline" onFinish={setOwner}>
          <Form.Item name="email" rules={[{ type: 'email', required: true }]} style={{ marginBottom: 8 }}>
            <Input placeholder="Existing user email" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit">Set owner</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card {...sectionCardProps} title="Invite Admin">
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={inviteAdmin}
          initialValues={{ roles: ['RDS Admin'] }}
          className={platformFormClassName}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', required: true }]} style={formItemStyle}>
                <Input placeholder="Invite email" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="roles" label="Roles" rules={[{ required: true }]} style={formItemStyle}>
                <Select
                  mode="multiple"
                  options={[
                    { value: 'RDS Owner', label: 'RDS Owner' },
                    { value: 'RDS Admin', label: 'RDS Admin' },
                    { value: 'Dispatcher', label: 'Dispatcher' },
                    { value: 'Store Operator', label: 'Store Operator' },
                    { value: 'Customer Manager', label: 'Customer Manager' },
                    { value: 'Report Manager', label: 'Report Manager' },
                    { value: 'Marketing Manager', label: 'Marketing Manager' },
                    { value: 'Restaurant Admin', label: 'Restaurant Admin' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="first_name" label={optionalLabel('First name')} style={formItemStyle}>
                <Input placeholder="Jane" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="last_name" label={optionalLabel('Last name')} style={formItemStyle}>
                <Input placeholder="Doe" />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit">Invite</Button>
        </Form>
        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          dataSource={admins}
          pagination={false}
          locale={{ emptyText: 'No admins yet' }}
          columns={[
            { title: 'Email', dataIndex: 'email' },
            {
              title: 'Name',
              render: (_, row) => {
                const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
                if (name) return name;
                return row.is_active ? '—' : (
                  <Text type="secondary">Pending sign-up</Text>
                );
              },
            },
            {
              title: 'Status',
              dataIndex: 'is_active',
              width: 140,
              render: (v) => (
                v ? (
                  <Tag color="success">Active</Tag>
                ) : (
                  <Tag>Invitation sent</Tag>
                )
              ),
            },
          ]}
        />
      </Card>

      <Card {...sectionCardProps} title="Restaurant owner (Connect)">
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Set the restaurant owner in console. That email becomes the Stripe Connect identity.
        </Text>
        <Button
          onClick={() => openConsoleForOrg({
            org,
            setOrganization,
            navigate,
            path: deepLinks.restaurant_id
              ? `/console/restaurants/${deepLinks.restaurant_id}`
              : '/console/restaurants/create',
          })}
        >
          {deepLinks.restaurant_id ? 'Open restaurant in console' : 'Create restaurant in console'}
        </Button>
      </Card>
    </Space>
  );
}

function PlatformClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setOrganization } = useContext(AdminContext);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabFromUrl = searchParams.get('tab');
  const activeTab = CLIENT_TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : 'overview';

  const setActiveTab = useCallback((key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!key || key === 'overview') next.delete('tab');
      else next.set('tab', key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Refreshes after Setup/Billing/SMS actions must not unmount the tabs, otherwise
  // the user is thrown back to Overview mid-flow.
  const load = useCallback((options = {}) => {
    if (!options.silent) setLoading(true);
    return fetchPlatformOrganization(id)
      .then(setOrg)
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => {
        if (!options.silent) setLoading(false);
      });
  }, [id]);

  const refresh = useCallback(() => load({ silent: true }), [load]);

  useEffect(() => { load(); }, [load]);

  const onOpenConsole = () => {
    openConsoleForOrg({
      org,
      setOrganization,
      navigate,
      path: '/console',
    });
  };

  const onMarkedGoLive = (readiness) => {
    setOrg((prev) => (prev ? { ...prev, readiness: readiness || prev.readiness } : prev));
  };

  if (loading || !org) {
    return (
      <Layout
        header={(
          <Layout.Header
            title="Client"
            backTitle="Clients"
            backPath="/platform/clients"
            loading
          />
        )}
      >
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      </Layout>
    );
  }

  const items = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <OverviewTab
          org={org}
          onOpenConsole={onOpenConsole}
          onSwitchTab={setActiveTab}
          onMarkedGoLive={onMarkedGoLive}
        />
      ),
    },
    { key: 'general', label: 'General', children: <GeneralTab org={org} onSaved={setOrg} /> },
    {
      key: 'setup',
      label: 'Delivery fees',
      children: <SetupTab org={org} onSaved={refresh} />,
    },
    { key: 'dispatchers', label: 'Dispatchers', children: <DispatchersTab org={org} onSaved={setOrg} /> },
    { key: 'branding', label: 'Branding', children: <BrandingTab org={org} onSaved={setOrg} /> },
    { key: 'stripe', label: 'Stripe', children: <StripeTab org={org} onSaved={setOrg} /> },
    { key: 'billing', label: 'Billing', children: <BillingTab org={org} onSaved={refresh} /> },
    { key: 'sms', label: 'SMS', children: <SmsTab org={org} onSaved={refresh} /> },
    { key: 'people', label: 'People', children: <PeopleTab org={org} onSaved={refresh} /> },
  ];

  const tabBarItems = items.map(({ key, label }) => ({ key, label }));
  const activeContent = items.find((item) => item.key === activeTab)?.children;

  return (
    <Layout
      header={(
        <Layout.Header
          title={org.name}
          backTitle="Clients"
          backPath="/platform/clients"
          tag={<StatusTags org={org} />}
        />
      )}
      subheader={(
        <Tabs
          className="platform-client-detail-tabs"
          items={tabBarItems}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      )}
    >
      {activeContent}
    </Layout>
  );
}

export default PlatformClientDetail;
